import { MutableRefObject, useCallback, memo, useState } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList
} from "@/components/adg4/tabs/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

import { TWC_TAB_DATA, TWCTab } from "./twc-tab-data";

const tabsWrapperStyle = css({
    marginInlineStart: "8px",
    marginInlineEnd: "8px"
});

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "10px"
});
const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});
const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const fadeInSlideUp = keyframes({
    from: {
        opacity: 0,
        transform: "translateY(10px)"
    },
    to: {
        opacity: 1,
        transform: "translateY(0)"
    }
});

const teamCardStyles = css({
    "boxSizing": "border-box",
    "border": `1px solid transparent`,
    "display": "flex",
    "flexDirection": "column",
    "width": "170px",
    "height": "92px",
    "alignItems": "center",
    "justifyContent": "center",
    "gap": "8px",
    "padding": "3px 6px",

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".gesture-icon": {
            animation: `${fadeIn} 0.5s ease-out forwards`
        }
    },

    "&[data-gesture-slide-up='true']:hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".gesture-icon": {
            animation: `${fadeInSlideUp} 0.5s ease-out forwards`
        }
    }
});

const teamCardActiveStyles = css({
    "border": `1px solid ${wacToken("color.primary.black")}`,
    "borderRadius": "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".gesture-icon": {
        opacity: 1
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "span": {
        fontWeight: "700"
    }
});

const iconWrapperStyles = css({
    position: "relative"
});

const iconContainerStyles = css({
    height: "48px",
    width: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const iconGestureStyles = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    opacity: 0
});

const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: wacToken("color.primary.black")
});

const imageContainerStyles = css({
    width: "100%",
    aspectRatio: "16/9",
    position: "relative",
    marginBlockEnd: "8%",

    [media.above.md]: {
        paddingInlineStart: "20px",
        paddingInlineEnd: "20px"
    }
});
const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-50px",
    insetBlockStart: "47%",
    width: "calc(100% + 100px)",
    height: "55%",
    backgroundColor: wacToken("color.primary.blue"),
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 16%,49% 10%,50% 26%,98.5% 13%,100% 85%,22.5% 100%,22.25% 85%,0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-20px",
        width: "calc(100% + 40px)"
    }
});

const heroImageStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img, video": {
        opacity: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        aspectRatio: "16/9",
        position: "absolute",
        insetBlockStart: 0,
        insetInlineStart: 0,
        maxWidth: "100%",
        maxHeight: "100%"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "video.active-hero-image": {
        opacity: 1,
        marginBlockStart: "50px",
        borderRadius: 8,
        boxShadow: token("elevation.shadow.overlay")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            opacity: 1
        }
    }
});

type TabsProps = {
    tabsWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

// Memoized TabButton component for better performance
const TabButton = memo(
    ({
        tab,
        index,
        isActive,
        onTabClick
    }: {
        tab: TWCTab;
        index: number;
        isActive: boolean;
        onTabClick: (index: number) => void;
    }) => {
        const intl = useIntl();

        return (
            <Adg4DraggableTab
                key={tab.id}
                tabIndex={index}
                onClick={() => onTabClick(index)}
            >
                <button
                    css={buttonResetStyle}
                    data-product-key="home"
                    data-event-component="button"
                    data-event-container="heroSection"
                    data-event="clicked"
                    data-label-english={tab.englishLabel}
                    aria-label={intl.formatMessage(tab.title)}
                >
                    <div
                        css={[teamCardStyles, isActive && teamCardActiveStyles]}
                        data-gesture-slide-up={
                            tab.id === "plan-and-track" ||
                            tab.id === "ai-teammates"
                                ? "true"
                                : "false"
                        }
                    >
                        <div css={iconWrapperStyles}>
                            <div
                                css={[iconContainerStyles]}
                                className="default-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.icon,
                                        alt: "",
                                        draggable: "false",
                                        width: tab.iconDimensions.width,
                                        height: tab.iconDimensions.height
                                    }}
                                    forceEagerLoad={true} // Icons are small, load eagerly
                                />
                            </div>
                            <div
                                css={[iconContainerStyles, iconGestureStyles]}
                                className="gesture-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.iconGesture,
                                        alt: "",
                                        draggable: "false",
                                        width: tab.iconDimensions.width,
                                        height: tab.iconDimensions.height,
                                        loading: "eager",
                                        fetchpriority: "low"
                                    }}
                                />
                            </div>
                        </div>
                        <span css={teamCardTextStyles}>
                            {intl.formatMessage(tab.title)}
                        </span>
                    </div>
                </button>
            </Adg4DraggableTab>
        );
    }
);

TabButton.displayName = "TabButton";

// Memoized HeroImageSection component
const HeroImageSection = memo(
    ({
        isCurrentTab,
        isInitialLoad
    }: {
        isCurrentTab: (index?: number) => boolean;
        isInitialLoad: boolean;
    }) => {
        const images = (
            <>
                {/* render video preview image and video first */}
                {
                    <Row layout="10">
                        <Video
                            videoLink="https://dam-cdn.atl.orangelogic.com/AssetLink/eu28pc4e878343vnpg2oxq0agjibv336.mp4"
                            videoType="mp4"
                            videoPreviewImage="https://dam-cdn.atl.orangelogic.com/AssetLink/p6tm7xy6w8hg08jx772v11u8tesvl456.webp"
                            loop={true}
                            muted={true}
                            aspectRatio="16/9"
                            className={isInitialLoad ? "active-hero-image" : ""}
                            fallbackText=""
                        />
                    </Row>
                }
                {TWC_TAB_DATA.map((tab, index) => {
                    return (
                        <Picture
                            key={index}
                            image={{
                                src: tab.heroImage,
                                alt: tab.englishLabel,
                                width: tab.heroImageDimensions.imageWidth,
                                height: tab.heroImageDimensions.imageHeight
                            }}
                            forceEagerLoad={index === 0} // Only first image loads eagerly
                            className={
                                isCurrentTab(index) && !isInitialLoad
                                    ? "active-hero-image"
                                    : ""
                            }
                        />
                    );
                })}
            </>
        );

        return (
            <Row layout="12">
                <div>
                    <div css={imageContainerStyles}>
                        <div css={heroImageStyles}>{images}</div>
                        <div css={backgroundStyles} />
                    </div>
                </div>
            </Row>
        );
    }
);
HeroImageSection.displayName = "HeroImageSection";

export const TWCTabs = memo(({ tabsWrapperRef }: TabsProps) => {
    const { goToTab, isCurrentTab } = useAdg4TabsContext();
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const handleTabClick = useCallback(
        (index: number) => {
            setIsInitialLoad(false);
            goToTab(index);
        },
        [goToTab]
    );

    return (
        <>
            <div css={tabsWrapperStyle} ref={tabsWrapperRef}>
                <Adg4DraggableTabList css={[tabsStyle]}>
                    {TWC_TAB_DATA.map((tab, index) => (
                        <TabButton
                            key={tab.id}
                            tab={tab}
                            index={index}
                            isActive={isCurrentTab(index) && !isInitialLoad}
                            onTabClick={handleTabClick}
                        />
                    ))}
                </Adg4DraggableTabList>
            </div>
            <HeroImageSection
                isCurrentTab={isCurrentTab}
                isInitialLoad={isInitialLoad}
            />
        </>
    );
});

TWCTabs.displayName = "TWCTabs";
